import type { ChipTypeMap } from "@mui/material";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import type {
  Components,
  PaletteColor,
  PaletteColorChannel,
  Theme,
} from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import type { ColorName } from "../../options/palette";

import { INPUT_PADDING_SPACING } from "./input-base";
import { DROP_DOWN_SPACING } from "./select";

declare module "@mui/material" {
  interface AutocompleteProps<
    /* eslint-disable @typescript-eslint/no-unused-vars */
    Value,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
    ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
    /* eslint-enable @typescript-eslint/no-unused-vars */
  > {
    color?: ColorName;
  }
}

const ADORNMENT_ALLOWANCE = {
  clear: "--AdornmentAllowance-clear",
  popup: "--AdornmentAllowance-popup",
};

export const MuiAutocomplete: Components<Theme>["MuiAutocomplete"] = {
  defaultProps: {
    autoHighlight: true,
    clearOnBlur: true,
    forcePopupIcon: true,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      [ADORNMENT_ALLOWANCE.clear]: "0px",
      [ADORNMENT_ALLOWANCE.popup]: "0px",

      "&&& .MuiAutocomplete-inputRoot": {
        padding: 0,
        paddingLeft: theme.spacing(INPUT_PADDING_SPACING),
        paddingRight: `calc(${theme.spacing(INPUT_PADDING_SPACING)} + var(${ADORNMENT_ALLOWANCE.clear}) + var(${ADORNMENT_ALLOWANCE.popup}))`,

        "& .MuiAutocomplete-input": {
          padding: 0,
        },
      },
    }),

    hasClearIcon: ({ theme }) => ({
      [ADORNMENT_ALLOWANCE.clear]: theme.spacing(DROP_DOWN_SPACING),
    }),

    hasPopupIcon: ({ theme }) => ({
      [ADORNMENT_ALLOWANCE.popup]: theme.spacing(DROP_DOWN_SPACING),
    }),

    listbox: ({ theme }) => ({
      padding: 0,

      variants: Object.entries(theme.palette)
        .filter(([_, values]) => typeof values === "object" && "main" in values)
        .map(([color, values]: [ColorName, PaletteColor]) => {
          const cssColors = theme.vars?.palette[color] as
            | PaletteColorChannel
            | undefined;

          return {
            props: {
              color,
            },
            style: {
              /**
               * These styles are pulled directly from MUI's Autocomplete implementation
               * The only change is replacing hard-coded references to `primary`
               * with the custom cssColors/values
               */
              [`& .${autocompleteClasses.option}[aria-selected="true"]`]: {
                backgroundColor: cssColors
                  ? `rgba(${cssColors.mainChannel} / ${theme.vars!.palette.action.selectedOpacity})`
                  : alpha(values.main, theme.palette.action.selectedOpacity),
                [`&.${autocompleteClasses.focused}`]: {
                  backgroundColor: cssColors
                    ? `rgba(${cssColors.mainChannel} / calc(${theme.vars!.palette.action.selectedOpacity} + ${theme.vars!.palette.action.hoverOpacity}))`
                    : alpha(
                        values.main,
                        theme.palette.action.selectedOpacity +
                          theme.palette.action.hoverOpacity,
                      ),
                },
                [`&.${autocompleteClasses.focusVisible}`]: {
                  backgroundColor: cssColors
                    ? `rgba(${cssColors.mainChannel} / calc(${theme.vars!.palette.action.selectedOpacity} + ${theme.vars!.palette.action.focusOpacity}))`
                    : alpha(
                        values.main,
                        theme.palette.action.selectedOpacity +
                          theme.palette.action.focusOpacity,
                      ),
                },
              },
            },
          };
        }),
    }),

    popupIndicator: ({ theme }) => ({
      transition: theme.transitions.create("all", {
        duration: theme.transitions.duration.shorter,
      }),
    }),

    tag: {
      margin: 0,
    },
  },
};
